import { isNotNullish, isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { isLehrkraft, Taetigkeiten } from '@martinus/taetigkeit/enums/Taetigkeiten.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { Role } from '../../../../permissions/roles.js';
import type { Person } from '../../../../users/entities/person.js';
import { legal } from '../../../../legal.js';
import { calculateWorkingHoursForEingruppierung } from '../../../api/calculateWorkingHours.js';
import {
  changeEndDateError,
  changeEndDateHint,
  changeStartDateError,
  changeStartDateHint,
} from '../../../api/contractTimeframes.js';
import { getHistoryForPath } from '../../../api/getHistoryForPath.js';
import { getReadableHistoryStrings } from '../../../api/getReadableHistoryStrings.js';
import { FULL_TIME_WORKING_HOURS } from '../../../constants/fullTimeWorkingHours.js';
import { MaxDeputatsStunden } from '../../../constants/maxDeputatStunden.js';
import type { AppliedContract } from '../../../entities/AppliedContract.js';
import {
  AGEingruppierung1,
  AGEingruppierung2,
} from '../../../entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';
import type { Zahlung } from '../../../entities/vertrag-sektionen/Zahlung.js';
import { Entgeltart, isTypeWithTariff } from '../../../enums/Entgeltart.js';
import { Tarifgruppe } from '../../../enums/Tarifgruppe.js';
import { canEditContractDatesFreely } from './contractValidators.js';
import {
  type ContractForm,
  type EingruppierungPath,
  ErfahrungsstufeText,
  KostenstellenEditor,
} from './sharedContractEditParts.js';

export interface SupplementIntervallDates {
  startDateAG1: Maybe<Date>;
  endDateAG1: Maybe<Date>;
  startDateAG2: Maybe<Date>;
  endDateAG2: Maybe<Date>;
}

interface ContractEditSimpleProps {
  form: ContractForm;
  contract: Maybe<AppliedContract>;
  person: Maybe<Person>;
  message?: string;
  hasEditPermission: boolean;
  canEdit: boolean;
  isInitialProcessFinished: boolean;
  supplementDates: SupplementIntervallDates;
  onSupplementDatesChanged: (dates: SupplementIntervallDates) => void;
  onKuendigungSet: (date: Date) => void;
  onChanged: () => void;
}

const SupplementIntervallFields = (
  props: ContractEditSimpleProps,
  suffix: '1' | '2',
  options: {
    showEnddates: boolean;
    setShowEnddates: (value: boolean) => void;
    visible: boolean;
  },
) => {
  if (!options.visible) return nothing;

  const startKey = suffix === '1' ? 'startDateAG1' : 'startDateAG2';
  const endKey = suffix === '1' ? 'endDateAG1' : 'endDateAG2';

  return html`
    ${FormGridEntry({
      label: 'Beginn der Änderung',
      render: html`
        <div class="flex flex-row items-center gap-2">
          <div class="flex-1 min-w-0">
            ${DateInput({
              disabled: !props.canEdit,
              clearable: true,
              placeholder: 'Wann erfolgt die Änderung?',
              placeholderMode: 'hide',
              value: props.supplementDates[startKey] ?? undefined,
              // the input keeps whatever the user enters, the rule is reported as an error underneath it
              error: changeStartDateError(props.supplementDates[startKey]),
              onPicked: (date: Date | null) => {
                props.onSupplementDatesChanged({ ...props.supplementDates, [startKey]: date });
              },
            })}
          </div>
          <span class="text-sm text-base-content/70 whitespace-nowrap">${changeStartDateHint}</span>
        </div>
      `,
    })}
    ${FormGridEntry({
      label: 'Ende der Änderung',
      labelAddition: ToggleInput({
        disabled: !props.canEdit,
        value: options.showEnddates,
        onPicked: (value: boolean | null) => {
          options.setShowEnddates(!!value);
          props.onSupplementDatesChanged({ ...props.supplementDates, [endKey]: null });
        },
      }),
      render: !options.showEnddates
        ? html`<p>Befristet?</p>`
        : html`
            <div class="flex flex-row items-center gap-2">
              <div class="flex-1 min-w-0">
                ${DateInput({
                  disabled: !props.canEdit,
                  clearable: true,
                  placeholder: 'Bis wann gilt die Änderung?',
                  placeholderMode: 'hide',
                  value: props.supplementDates[endKey] ?? undefined,
                  // the end of a change may not precede the start of the same eingruppierung
                  error: changeEndDateError(props.supplementDates[endKey], props.supplementDates[startKey]),
                  onPicked: (date: Date | null) => {
                    props.onSupplementDatesChanged({ ...props.supplementDates, [endKey]: date });
                  },
                })}
              </div>
              <span class="text-sm text-base-content/70 whitespace-nowrap">${changeEndDateHint}</span>
            </div>
          `,
    })}
  `;
};

const SimpleEingruppierungSection = stateful(
  (props: ContractEditSimpleProps & { suffix: '1' | '2'; user: Maybe<Person> }) => {
    const path: EingruppierungPath = `agEingruppierung${props.suffix}`;

    // the toggles are on whenever their value is set, the state only covers "opened but still empty". A recalculated
    // applied contract is merged into the same instance by the entity store, so state keyed on it would never reset
    // and would miss values a supplement brings in.
    const [enddatesOpened, setEnddatesOpened] = useState(false, [props.contract?._id]);
    const showEnddates =
      enddatesOpened || !!(props.suffix === '1' ? props.supplementDates.endDateAG1 : props.supplementDates.endDateAG2);
    const [teilzeitOpened, setTeilzeitOpened] = useState(false, [props.contract?._id]);
    const showTeilzeit = teilzeitOpened || isNotNullish(props.form.document?.[path]?.agArbeitszeit?.zaehler);

    const user = props.user;
    const isSchulleiterOrMav = !!user?.roles.find(r => r.name === Role.SCHULLEITER || r.name === Role.MAV);
    const intervallFieldsVisible = props.isInitialProcessFinished && isSchulleiterOrMav;

    const eingruppierung = props.form.document?.[path];
    const taetigkeit = eingruppierung?.agVertragsInfos?.taetigkeit;
    const isLehrer = isLehrkraft(taetigkeit);

    const historyFor = (subPath: string) =>
      props.canEdit
        ? getReadableHistoryStrings(getHistoryForPath(props.contract?.history, `${path}.${subPath}`))
        : undefined;

    const appliedStart = props.form.document?.[`appliedSupplementStartAG${props.suffix}`];
    const appliedEnd = props.form.document?.[`appliedSupplementEndAG${props.suffix}`];

    const intervallFields = SupplementIntervallFields(props, props.suffix, {
      showEnddates,
      setShowEnddates: setEnddatesOpened,
      visible: intervallFieldsVisible,
    });

    return html`
      <div class="flex flex-col gap-3">
        ${CardHeader({
          divider: false,
          title: `Eingruppierung ${props.suffix}`,
          actionsDivider: true,
          actions:
            props.suffix === '2' && props.form.document?.agEingruppierung2 && props.hasEditPermission
              ? html`
                  <button
                    class="btn btn-ghost btn-sm btn-square"
                    aria-label="Eingruppierung 2 löschen"
                    ?disabled=${!props.canEdit}
                    @click=${() => {
                      if (!props.canEdit || !props.form.document) return;
                      props.form.document.agEingruppierung2 = null;
                      props.onChanged();
                    }}
                  >
                    <span class="icon-[material-symbols--delete-outline] text-error text-lg"></span>
                  </button>
                `
              : nothing,
        })}
        ${
          !props.contract?.[path] && props.hasEditPermission
            ? html`
                <div class="grid grid-cols-1 md:grid-cols-[1fr_4fr] items-center gap-2">${intervallFields}</div>
                <button
                  class="btn btn-dash btn-block justify-start ${
                    !props.canEdit ? 'btn-disabled pointer-events-none opacity-50' : ''
                  }"
                  @click=${() => {
                    if (!props.canEdit || !props.form.document) return;
                    props.form.document[path] =
                      props.suffix === '1' ? new AGEingruppierung1({}) : new AGEingruppierung2({});

                    // in the simple mode we want to prefill the stunden in arbeitszeit. This is not done via a default value in the orm because it would destroy some exports which create the subdate if it is undefined
                    if (props.form.document[path]?.agArbeitszeit) {
                      props.form.document[path].agArbeitszeit.stunden = FULL_TIME_WORKING_HOURS;
                    }
                    props.onChanged();
                  }}
                >
                  <span class="icon-[material-symbols--add]"></span>
                  Eingruppierung hinzufügen
                </button>
              `
            : props.contract?.[path]
              ? html`
                  <div class="grid grid-cols-1 md:grid-cols-[1fr_4fr] items-center gap-2">
                    ${intervallFields}
                    ${FormGridEntry({
                      label: 'Gültig ab',
                      render:
                        appliedStart || intervallFieldsVisible
                          ? html`
                              <p>
                                ${
                                  appliedStart
                                    ? `${formatCalendarDay(appliedStart, 'dd.MM.yyyy')} von Nachtrag ${props.form.document?.lastAppliedSupplement}`
                                    : `${
                                        props.form.document?.agInfos?.einstellungsdatum
                                          ? formatCalendarDay(
                                              props.form.document.agInfos.einstellungsdatum,
                                              'dd.MM.yyyy',
                                            )
                                          : '---'
                                      } von Vertrag`
                                }
                              </p>
                            `
                          : DateInput({
                              disabled: !props.canEdit,
                              clearable: true,
                              placeholder: 'Wann tritt die Eingruppierung in Kraft?',
                              placeholderMode: 'hide',
                              value:
                                eingruppierung?.agVertragsInfos?.startDate ??
                                props.form.document?.agInfos?.einstellungsdatum ??
                                undefined,
                              onPicked: (date: Date | null) => {
                                if (!eingruppierung?.agVertragsInfos) return;
                                eingruppierung.agVertragsInfos.startDate = date;
                                props.onChanged();
                              },
                            }),
                    })}
                    ${FormGridEntry({
                      label: 'Gültig bis',
                      render:
                        appliedEnd || appliedStart || intervallFieldsVisible
                          ? html`
                              <p>
                                ${
                                  appliedEnd
                                    ? `${formatCalendarDay(appliedEnd, 'dd.MM.yyyy')} von Nachtrag ${props.form.document?.lastAppliedSupplement}`
                                    : `${
                                        props.form.document?.agInfos?.gekuendigtZum
                                          ? formatCalendarDay(props.form.document.agInfos.gekuendigtZum, 'dd.MM.yyyy')
                                          : 'Offenes Ende'
                                      } von Vertrag`
                                }
                              </p>
                            `
                          : DateInput({
                              disabled: !props.canEdit,
                              clearable: true,
                              placeholder: 'Offenes Ende',
                              placeholderMode: 'hide',
                              value:
                                eingruppierung?.agVertragsInfos?.endDate ??
                                props.form.document?.agInfos?.gekuendigtZum ??
                                undefined,
                              onPicked: (date: Date | null) => {
                                if (!eingruppierung?.agVertragsInfos) return;
                                eingruppierung.agVertragsInfos.endDate = date;
                                props.onChanged();
                              },
                            }),
                    })}

                    <hr class="col-span-full border-base-300" />

                    ${FormGridEntry({
                      label: 'Tätigkeit',
                      render: DropdownSelectionInput({
                        ...props.form.field(path, 'agVertragsInfos', 'taetigkeit'),
                        placeholder: 'Tätigkeit',
                        clearable: true,
                        selectables: Object.values(Taetigkeiten),
                        onPicked: (value: Taetigkeiten | null) => {
                          props.form.field(path, 'agVertragsInfos', 'taetigkeit').onPicked?.(value);

                          const arbeitszeit = props.form.document?.[path]?.agArbeitszeit;
                          if (arbeitszeit) {
                            arbeitszeit.stunden = null;
                            arbeitszeit.maxDeputat = null;

                            if (!isLehrkraft(value)) {
                              arbeitszeit.stunden = FULL_TIME_WORKING_HOURS;
                            }
                          }
                          props.onChanged();
                        },
                      }),
                      history: historyFor('agVertragsInfos.taetigkeit'),
                    })}
                  </div>
                  <hr class="border-base-300" />

                  ${Collapse({
                    className: 'border-0 bg-transparent shadow-none',
                    heading: html`<h4 class="text-base font-semibold">Arbeitszeit</h4>`,
                    defaultOpen: true,
                    content: html`
                      <div class="grid grid-cols-1 md:grid-cols-[1fr_4fr] items-center gap-2">
                        ${FormGridEntry({
                          label: `${isLehrer ? 'Deputat' : 'Stunden'} pro Woche`,
                          labelAddition: ToggleInput({
                            disabled: !props.canEdit,
                            value: showTeilzeit,
                            onPicked: (value: boolean | null) => {
                              setTeilzeitOpened(!!value);
                              if (!value && props.form.document?.[path]?.agArbeitszeit) {
                                props.form.document[path].agArbeitszeit.zaehler = null;
                              }
                              props.onChanged();
                            },
                          }),
                          render: showTeilzeit
                            ? NumberInput({
                                ...props.form.field(path, 'agArbeitszeit', 'zaehler'),
                                placeholder: 'Keine Stunden pro Woche hinterlegt',
                                placeholderMode: 'hide',
                                clearable: true,
                                min: 0,
                              })
                            : html`<p>Teilzeit?</p>`,
                          history: showTeilzeit ? historyFor('agArbeitszeit.zaehler') : undefined,
                        })}

                        <hr class="col-span-full border-base-300" />

                        ${
                          isLehrer
                            ? FormGridEntry({
                                label: 'Vollzeit Deputat',
                                render: DropdownSelectionInput({
                                  ...props.form.field(path, 'agArbeitszeit', 'maxDeputat'),
                                  placeholder: 'Kein maximales Deputat ausgewählt',
                                  selectables: Object.values(MaxDeputatsStunden).filter(
                                    v => taetigkeit !== Taetigkeiten.LEHRKRAFT || (v !== 33 && v !== 30 && v !== 39),
                                  ) as number[],
                                  clearable: true,
                                  onPicked: (value: number | null) => {
                                    props.form.field(path, 'agArbeitszeit', 'maxDeputat').onPicked?.(value);
                                    if (!isNullish(value) && props.form.document?.[path]?.agArbeitszeit) {
                                      props.form.document[path].agArbeitszeit.stunden = null;
                                    }
                                    props.onChanged();
                                  },
                                }),
                                history: historyFor('agArbeitszeit.maxDeputat'),
                              })
                            : FormGridEntry({
                                label: 'Stundenumfang in Vollzeit (pro Woche)',
                                render: NumberInput({
                                  ...props.form.field(path, 'agArbeitszeit', 'stunden'),
                                  placeholder: '(nicht-Lehrkräfte)',
                                  placeholderMode: 'hide',
                                  clearable: true,
                                  min: 0,
                                  onPicked: (value: number | null) => {
                                    props.form.field(path, 'agArbeitszeit', 'stunden').onPicked?.(value);
                                    if (!isNullish(value) && props.form.document?.[path]?.agArbeitszeit) {
                                      props.form.document[path].agArbeitszeit.maxDeputat = null;
                                    }
                                    props.onChanged();
                                  },
                                }),
                                history: historyFor('agArbeitszeit.maxDeputat'),
                              })
                        }
                        ${
                          !isLehrer
                            ? FormGridEntry({
                                label: 'Ferien einarbeiten?',
                                render: ToggleInput({
                                  ...props.form.field(path, 'agArbeitszeit', 'ferienEinarbeiten'),
                                  label: eingruppierung?.agArbeitszeit?.ferienEinarbeiten ? 'Ja' : 'Nein',
                                  onPicked: (value: boolean | null) => {
                                    props.form.field(path, 'agArbeitszeit', 'ferienEinarbeiten').onPicked?.(value);
                                    props.onChanged();
                                  },
                                }),
                                history: historyFor('agArbeitszeit.ferienEinarbeiten'),
                              })
                            : nothing
                        }
                        ${
                          eingruppierung?.agArbeitszeit?.ferienEinarbeiten && !isLehrer
                            ? FormGridEntry({
                                label: 'Bezahlter Stundenumfang',
                                render: html`
                                  <p>
                                    <b>
                                      ${eingruppierung ? calculateWorkingHoursForEingruppierung(eingruppierung).toFixed(2) : 0}
                                    </b>
                                    Stunden
                                  </p>
                                `,
                              })
                            : nothing
                        }
                        ${FormGridEntry({
                          label: 'Zu leistender Stundenumfang',
                          render: html`
                            <p>
                              <b>
                                ${
                                  eingruppierung
                                    ? calculateWorkingHoursForEingruppierung(eingruppierung, true).toFixed(2)
                                    : 0
                                }
                              </b>
                              Stunden sind
                              <span>
                                ${
                                  eingruppierung?.agArbeitszeit?.maxDeputat || eingruppierung?.agArbeitszeit?.stunden
                                    ? (eingruppierung.agArbeitszeit?.zaehler
                                        ? (eingruppierung.agArbeitszeit.zaehler /
                                            (eingruppierung.agArbeitszeit.maxDeputat ??
                                              eingruppierung.agArbeitszeit.stunden ??
                                              1)) *
                                          100
                                        : 100
                                      ).toFixed(2)
                                    : 'XX'
                                }%
                                einer Vollzeitstelle
                              </span>
                            </p>
                          `,
                        })}
                      </div>
                    `,
                  })}

                  <div class="grid grid-cols-1 md:grid-cols-[1fr_4fr] items-start gap-2">
                    <span class="text-sm font-semibold text-base-content/70">Kostenstellen</span>
                    ${KostenstellenEditor({
                      form: props.form,
                      contract: props.contract,
                      path,
                      hasEditPermission: props.hasEditPermission,
                      canEdit: props.canEdit,
                      onChanged: props.onChanged,
                    })}
                  </div>

                  ${
                    ((isTypeWithTariff(eingruppierung?.agLohnGehaltszahlungen?.entgeltart as Entgeltart) &&
                      eingruppierung?.agLohnGehaltszahlungen?.tarifgruppe) ||
                      eingruppierung?.agLohnGehaltszahlungen?.monatlicheZahlungen?.at(0)?.betrag) &&
                    (!props.hasEditPermission || user?.roles.find(r => r.name === Role.SCHULLEITER))
                      ? html`
                          <hr class="border-base-300" />
                          ${Collapse({
                            className: 'border-0 bg-transparent shadow-none',
                            heading: html`<h4 class="text-base font-semibold">Tarif</h4>`,
                            defaultOpen: true,
                            content: html`
                              <div class="grid grid-cols-1 md:grid-cols-[1fr_4fr] items-center gap-4">
                                ${
                                  isTypeWithTariff(eingruppierung?.agLohnGehaltszahlungen?.entgeltart as Entgeltart)
                                    ? html`
                                        ${FormGridEntry({
                                          label: 'Tarifgruppe',
                                          render: html`<p>${eingruppierung?.agLohnGehaltszahlungen?.tarifgruppe}</p>`,
                                        })}
                                        ${FormGridEntry({
                                          label: 'Erfahrungsstufe',
                                          render: ErfahrungsstufeText(props.contract, props.suffix),
                                        })}
                                      `
                                    : FormGridEntry({
                                        label: 'Entgeltart',
                                        render: html`<p>${eingruppierung?.agLohnGehaltszahlungen?.entgeltart}</p>`,
                                      })
                                }
                                ${
                                  eingruppierung?.agLohnGehaltszahlungen?.monatlicheZahlungen?.length
                                    ? FormGridEntry({
                                        label: 'Monatliche Zahlungen',
                                        render: html`
                                          <div class="flex flex-row flex-wrap gap-1">
                                            ${repeat(
                                              eingruppierung.agLohnGehaltszahlungen.monatlicheZahlungen ?? [],
                                              (z: Zahlung) => z._id,
                                              (z: Zahlung) => html`
                                                <div class="card p-1">
                                                  <p>
                                                    ${z.lohnart ?? ''}:
                                                    <b>
                                                      ${typeof z.betrag === 'number' ? z.betrag.toFixed(2) : z.betrag}
                                                    </b>
                                                    €
                                                    ${
                                                      z.monat && z.jahr
                                                        ? ` (${String(z.monat).padStart(2, '0')}/${z.jahr})`
                                                        : ''
                                                    }
                                                  </p>
                                                </div>
                                              `,
                                            )}
                                          </div>
                                        `,
                                      })
                                    : nothing
                                }
                              </div>
                            `,
                          })}
                        `
                      : !props.hasEditPermission || user?.roles.find(r => r.name === Role.SCHULLEITER)
                        ? html`
                            <hr class="border-base-300" />
                            <h4 class="font-semibold">Tarif</h4>
                            <p class="font-semibold text-warning">Noch nicht eingruppiert.</p>
                          `
                        : nothing
                  }
                  ${
                    props.hasEditPermission && !user?.roles.find(r => r.name === Role.SCHULLEITER)
                      ? html`
                          <hr class="border-base-300" />
                          ${Collapse({
                            className: 'border-0 bg-transparent shadow-none',
                            heading: html`<h4 class="text-base font-semibold">Lohn und Gehaltszahlung</h4>`,
                            defaultOpen: true,
                            content: html`
                              <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
                                ${FormGridEntry({
                                  label: 'Entgeltart',
                                  render: DropdownSelectionInput({
                                    ...props.form.field(path, 'agLohnGehaltszahlungen', 'entgeltart'),
                                    selectables: Object.values(Entgeltart),
                                    placeholder: 'Keine Entgeltart',
                                    clearable: true,
                                  }),
                                  history: historyFor('agLohnGehaltszahlungen.entgeltart'),
                                })}
                                ${FormGridEntry({
                                  label: 'Tarifgruppe',
                                  render: DropdownSelectionInput({
                                    ...props.form.field(path, 'agLohnGehaltszahlungen', 'tarifgruppe'),
                                    selectables: Object.values(Tarifgruppe),
                                    placeholder: 'Keine Tarifgruppe',
                                    clearable: true,
                                    onPicked: (value: Tarifgruppe | null) => {
                                      props.form.field(path, 'agLohnGehaltszahlungen', 'tarifgruppe').onPicked?.(value);
                                      props.onChanged();
                                    },
                                  }),
                                  history: historyFor('agLohnGehaltszahlungen.tarifgruppe'),
                                })}
                                ${FormGridEntry({
                                  label: 'Fiktives Datum für Beginn Stufe 1 bei höherer Eingruppierung',
                                  render: DateInput({
                                    ...props.form.field(path, 'agLohnGehaltszahlungen', 'fiktivBeginnStufe1'),
                                    placeholder: 'Kein fiktives Datum',
                                    placeholderMode: 'hide',
                                    clearable: true,
                                  }),
                                  history: historyFor('agLohnGehaltszahlungen.fiktivBeginnStufe1'),
                                })}
                                ${FormGridEntry({
                                  label: 'Erfahrungsstufe',
                                  render: ErfahrungsstufeText(props.contract, props.suffix),
                                })}
                              </div>
                            `,
                          })}
                        `
                      : nothing
                  }
                `
              : html`<p>Keine Eingruppierung ${props.suffix}</p>`
        }
      </div>
    `;
  },
);

/**
 * Simplified contract view for Schulleitung and MAV (and default view for everyone else).
 * Shows less fields and enforces the MAV process rules.
 */
export const ContractEditSimple = stateful((props: ContractEditSimpleProps) => {
  const user = useObservable(() => currentUser<Person>(), []);

  if (!props.contract) return html`<div class="card card-body"><div class="skeleton h-24 w-full"></div></div>`;

  const isSchulleiterOrMav = !!user?.roles.find(r => r.name === Role.SCHULLEITER || r.name === Role.MAV);
  const isMav = !!user?.roles.find(r => r.name === Role.MAV);

  const retirementDate = props.person?.profilDaten?.stammdaten?.geburtsDatum
    ? legal.retirement.calculateRetirement(props.person.profilDaten.stammdaten.geburtsDatum).retirementDate
    : null;

  const canEditDatesFreely = canEditContractDatesFreely(user);

  return html`
    <div class="flex flex-col gap-2">
      <div class="card card-body">
        <div class="flex flex-col gap-3">
          ${props.message ? html`<p class="font-semibold text-warning">${props.message}</p>` : nothing}
          <div class="grid grid-cols-1 md:grid-cols-[1fr_4fr] items-center gap-2">
            ${
              isSchulleiterOrMav && props.isInitialProcessFinished
                ? FormGridEntry({
                    label: 'Einstellungsdatum',
                    render: html`
                      <p class="py-1 font-semibold">
                        ${
                          props.form.document?.agInfos?.einstellungsdatum
                            ? formatCalendarDay(props.form.document.agInfos.einstellungsdatum, 'dd.MM.yyyy')
                            : '---'
                        }
                      </p>
                    `,
                  })
                : FormGridEntry({
                    label: 'Einstellungsdatum',
                    render: html`
                      <div class="flex flex-row items-center gap-2">
                        <div class="flex-1 min-w-0">
                          ${DateInput({
                            // the entered date is kept and reported by the form validator, see buildContractValidator
                            ...props.form.field('agInfos', 'einstellungsdatum'),
                            clearable: true,
                            placeholder: 'Wann erfolgt die Einstellung?',
                            placeholderMode: 'hide',
                          })}
                        </div>
                        <span class="text-sm text-base-content/70 whitespace-nowrap">${changeStartDateHint}</span>
                      </div>
                    `,
                    history: getReadableHistoryStrings(
                      getHistoryForPath(props.contract?.history, 'agInfos.einstellungsdatum'),
                    ),
                  })
            }
            ${
              props.form.document?._id && isMav
                ? FormGridEntry({
                    label: 'Vertrag beendet zum',
                    render: html`
                      <p class="py-1">
                        ${
                          props.form.document.agInfos?.gekuendigtZum
                            ? formatCalendarDay(props.form.document.agInfos.gekuendigtZum, 'dd.MM.yyyy')
                            : 'Offenes Ende'
                        }
                      </p>
                    `,
                  })
                : FormGridEntry({
                    label: 'Vertrag beendet zum',
                    render: html`
                      <div class="flex flex-row items-center gap-2">
                        <div class="flex-1 min-w-0">
                          ${DateInput({
                            // the entered date is kept and reported by the form validator, see buildContractValidator
                            ...props.form.field('agInfos', 'gekuendigtZum'),
                            disabled: !props.canEdit,
                            clearable: true,
                            placeholder: 'Wann erfolgt die Beendigung des Vertrages?',
                            placeholderMode: 'hide',
                            onPicked: async (date: Date | null) => {
                              if (!props.form.document?.agInfos) return;
                              const previous = props.form.document.agInfos.gekuendigtZum;
                              props.form.field('agInfos', 'gekuendigtZum').onPicked?.(date);

                              // ending the contract ends all of its supplements and saves right away, so that
                              // only runs for a date that holds up to the rules
                              if (
                                date &&
                                (canEditDatesFreely ||
                                  !changeEndDateError(date, props.form.document.agInfos.einstellungsdatum))
                              ) {
                                props.onKuendigungSet(date);
                              }
                              if (!date && previous) {
                                await alertDialog(
                                  'Achtung! Das vorherige setzen des Kündigungsdatums könnte sich auch auf Nachträge ausgewirkt haben. Bitte den Verlauf prüfen um etwaige Änderungen von Enddatumseinträgen rückgängig zu machen.',
                                );
                              }
                            },
                          })}
                        </div>
                        <div class="flex flex-col text-sm">
                          <span class="text-base-content/70 whitespace-nowrap">${changeEndDateHint}</span>
                          <span class="whitespace-nowrap">
                            Renteneintritt:
                            ${retirementDate ? formatCalendarDay(retirementDate, 'dd.MM.yyyy') : 'Kein Geburtsdatum'}
                          </span>
                        </div>
                      </div>
                    `,
                    history: getReadableHistoryStrings(
                      getHistoryForPath(props.contract?.history, 'agInfos.gekuendigtZum'),
                    ),
                  })
            }
          </div>
        </div>
      </div>

      <div class="card card-body">${SimpleEingruppierungSection({ ...props, suffix: '1', user })}</div>
      <div class="card card-body">${SimpleEingruppierungSection({ ...props, suffix: '2', user })}</div>
    </div>
  `;
});
